import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChildren } from '@angular/core';
import { SingleFocusService } from 'components/input/editable-content/editable-content.component';
import { DataRowComponent, DataTableComponent } from 'components/layout/data-table/data-table.component';
import { PrefixIconComponent } from 'components/layout/prefix-icon/prefix-icon.component';
import { OrderLinesComponent } from 'components/sale/order-lines.component';
import { Skeleton } from 'components/shared/skeleton/skeleton';
import { QuestionCategoryType } from 'enums/QuestionCategoryType';
import { ReplyType } from 'enums/ReplyType';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { QuestionCategoryViewModel, QuestionnaireViewModel } from 'models/generated/Models.QuestionnaireView';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription, forkJoin } from 'rxjs';
import { CompanyService } from 'services/company.service';
import { FormState, FormValidationService } from 'services/form-validation.service';
import { PushService } from 'services/push.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { SalesService } from 'services/sales.service';
import { QuestionnaireQuestionComponent } from './questionnaire-question.component';

@Component({
	selector: 'questionnaire',
	templateUrl: './questionnaire.component.html',
	styleUrls: ['./questionnaire.component.scss'],
	providers: [SingleFocusService, FormValidationService],
	imports: [PrefixIconComponent, NgTemplateOutlet, OrderLinesComponent, QuestionnaireQuestionComponent, Skeleton, DataTableComponent, DataRowComponent, TranslatePipe]
})
export class QuestionnaireComponent implements OnDestroy {

	public categoryType = QuestionCategoryType;

	public questionnaireLoaded = false;
	private allQuestionsAnswered = false;
	
	public questionnaire?: QuestionnaireViewModel;
	public orders = new Map<string, OrderModel>();

	private companyChangeSubscription: Subscription;
	private _orderLines?: OrderLineModel[];
	private _questionnaireId?: string;

	@ViewChildren(QuestionnaireQuestionComponent) questionComponents?: QuestionnaireQuestionComponent[];
	@Output() completed = new EventEmitter<boolean>();
	@Input() taskflowId?: string;
	@Input() editable = false;
	@Input() displayAsForm = false;
	@Input() showCategoryDetails = true;
	@Input() set orderLines(orderLines: OrderLineModel[] | undefined) {

		if (!orderLines) return;
		const distinctOrderIds = [...new Set(orderLines.map(orderLine => orderLine.orderId as string))];

		this.orders.clear();
		const loadOrders: Promise<void>[] = [];
		distinctOrderIds.forEach(id => {
			loadOrders.push(this.salesService.getOrderById(id).then(order => { this.orders.set(id, order); }));
		});
		forkJoin(loadOrders).subscribe(() => {
			this.orders.forEach(order => {
				order.orderLines.sort((a, b) => {
					if (a.productVariant!.product!.name == b.productVariant!.product!.name)
						return a.productVariant!.name.localeCompare(b.productVariant!.name);
					return a.productVariant!.product!.name.localeCompare(b.productVariant!.product!.name);
				});
			});
			this._orderLines = Array.from(this.orders.values())
				.map(order => order.orderLines)
				.reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
				.filter(orderLine => orderLines.map(op => op.id).indexOf(orderLine.id) != -1);
			if (this._questionnaireId !== '') {
				this.loadReplyQuestionnaire();
			}
		});
	}
	get orderLines(): OrderLineModel[] | undefined { return this._orderLines; }

	@Input() set questionnaireId(id: string) {
		if (!id) return;
		this._questionnaireId = id;
		this.loadReplyQuestionnaire();
	}
	@Input() set questionnaireView(view: QuestionnaireViewModel) {
		if (!view) return;
		this.questionnaire = view;
		this._questionnaireId = view.id;
		this.useQuestionnaireView(view);
	}

	public get showQuestionnaire(): boolean {
		return this.questionnaireLoaded && (this.questionnaire?.questionCategories.some(category => category.questionGroups.some(group => group.questions.length > 0)) ?? false);
	}

	public getCategoryIcon(category: QuestionCategoryViewModel): string {
		switch (category.categoryType) {
			case QuestionCategoryType.Company:
				return 'fa fa-home';
			case QuestionCategoryType.Order:
				return 'far fa-clipboard-list';
			case QuestionCategoryType.Product:
				return 'fa fa-cube';
			default:
				return '';
		}
	}

	constructor(
		private readonly questionnaireService: QuestionnaireService,
		private readonly pushService: PushService,
		private readonly companyService: CompanyService,
		private readonly salesService: SalesService,
		private readonly formValidation: FormValidationService) {

		this.formValidation.stateChanged.subscribe(state => {
			// Question views emit an "invalid" state until they are answered; they are never pristine
			const isValid = state != FormState.Invalid;
			if (isValid == this.allQuestionsAnswered) return;

			this.allQuestionsAnswered = isValid;
			this.completed.emit(isValid);
		});
	
		this.companyChangeSubscription = this.pushService.onCompanyChanges.subscribe(async (event) => {
			const companyGroups = this.questionnaire?.questionCategories.flatMap(c => c.questionGroups.filter(g => g.companyId == event.data.companyId));
			if (!companyGroups || companyGroups.length === 0) return;

			const company = await this.companyService.getCompany(event.data.companyId);
			companyGroups.forEach(group => group.company = company);
		});
	}

	public async saveAllReplies() {
		if (!this.questionComponents) return;
		const promises: Promise<void>[] = [];
		for (const questionComponent of this.questionComponents) {
			promises.push(questionComponent.updateReplies(questionComponent.replyGroups));
		}
		await Promise.all(promises);
	}

	public areRequiredQuestionsAnswered() : boolean {
		if (!this.questionComponents || this.questionComponents.length == 0) return false;
		let isAnswered = true;
		for (const questionComponent of this.questionComponents) {
			if (!questionComponent.question?.required) continue;
			if (questionComponent.loading || !questionComponent.isAnswered) isAnswered = false;
		}
		return isAnswered;
	}

	private loadReplyQuestionnaire() {
		if (this.orderLines === undefined || !this._questionnaireId) return;

		const orderLineIds = this.orderLines.map(orderLine => orderLine.id!);
		const distinctProductVariants = [... new Set(this.orderLines.map(orderLine => orderLine.productVariant!))];
		distinctProductVariants.forEach(variant => variant.orderLines.sort((a, b) => a.id!.localeCompare(b.id!)));

		this.questionnaire = undefined;
		this.questionnaireService.getQuestionnaireView(this._questionnaireId, orderLineIds, this.taskflowId).then(questionnaireView => {
			this.useQuestionnaireView(questionnaireView);
		});
	}

	private useQuestionnaireView(questionnaireView: QuestionnaireViewModel) {
		this.questionnaire = questionnaireView;
		if (!this.questionnaire) return;

		this.questionnaire.questionCategories.forEach(category => {
			category.questionGroups.forEach(questionGroup => {
				questionGroup.questions.sort((a, b) => {
					return (a.sortOrder == undefined ? 0 : a.sortOrder) - (b.sortOrder == undefined ? 0 : b.sortOrder);
				});
			});
		});

		// Get company IDs from groups in the Company category (we only need company data for that category)
		const distinctCompanyIds = this.questionnaire.questionCategories.flatMap(c => c.categoryType === QuestionCategoryType.Company ? c.questionGroups : []).map(group => group.companyId as string) ?? [];
		// And use them to complete data on all groups with that CompanyId
		distinctCompanyIds.forEach(companyId => {
			this.companyService.getCompany(companyId).then(company => {
				this.questionnaire?.questionCategories.flatMap(c => c.questionGroups.filter(g => g.companyId === companyId)).forEach(g => g.company = company);
			});
		});

		if (!this.editable) this.removeUnwantedQuestions(); // In read-only mode, certain questions will be hidden because they are irrelevant for the person reading replies

		this.questionnaireLoaded = true;
	}

	private removeUnwantedQuestions() {
		this.questionnaire?.questionCategories.flatMap(c => c.questionGroups).forEach(group => {
			group.questions = group.questions.filter(question => question.questionReplyInputType !== ReplyType.Checkbox);
		});
	}

	ngOnDestroy() {
		this.companyChangeSubscription.unsubscribe();
	}
}
